import {
  AppstoreOutlined,
  BarChartOutlined,
  DatabaseOutlined,
  DollarOutlined,
  SafetyOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useGetIdentity } from '@refinedev/core';
import { Button, Dropdown, type MenuProps, Space, Tag, Tooltip, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { navigateToAppName } from '../api/app-navigation.js';
import type { AuthUser } from '../api/auth-provider.js';
import { crestHttp } from '../api/client.js';
import { tx } from '../i18n/translate.js';
import { normalizePortalAppDisplayName } from './app-display-name.js';

const ICON_MAP: Record<string, React.ReactNode> = {
  briefcase: <AppstoreOutlined />,
  database: <DatabaseOutlined />,
  finance: <DollarOutlined />,
  reporting: <BarChartOutlined />,
  team: <TeamOutlined />,
  'shield-check': <SafetyOutlined />,
};

const ALWAYS_AVAILABLE = () => true;

interface AppItem {
  appid: string;
  name: string;
  display_name_zh: string;
  display_name_en: string;
  icon: string | null;
  color: string | null;
  is_default: boolean;
  display_order: number;
}

interface AppSwitcherProps {
  collapsed?: boolean;
  mode?: 'portal' | 'single';
  fixedAppName?: string;
  fixedLabelZh?: string;
  fixedLabelEn?: string;
  fixedIcon?: string;
  isAppAvailable?: (appName: string) => boolean;
}

export function AppSwitcher({
  collapsed = false,
  mode = 'portal',
  fixedAppName,
  fixedLabelZh,
  fixedLabelEn,
  fixedIcon,
  isAppAvailable = ALWAYS_AVAILABLE,
}: AppSwitcherProps) {
  const { data: user } = useGetIdentity<AuthUser>();
  const navigate = useNavigate();
  const { appName } = useParams<{ appName: string }>();
  const [apps, setApps] = useState<AppItem[]>([]);
  const { i18n } = useTranslation();
  const isEn = (i18n.language ?? '').toLowerCase().startsWith('en');
  const currentAppName = appName ?? fixedAppName;

  useEffect(() => {
    if (mode !== 'portal' || !user) return;
    crestHttp.get<AppItem[]>('/me/apps').then((r) => {
      const published = r.data
        .map(normalizePortalAppDisplayName)
        .filter((app) => isAppAvailable(app.name));
      setApps(published);
    });
  }, [isAppAvailable, mode, user]);

  const current =
    apps.find((a) => a.name === currentAppName) ?? apps.find((a) => a.is_default) ?? apps[0];
  const labelOf = (a: AppItem) => (isEn ? a.display_name_en : a.display_name_zh);

  if (mode === 'single') {
    const label =
      (isEn ? fixedLabelEn : fixedLabelZh) ??
      fixedLabelZh ??
      fixedLabelEn ??
      tx('legacy:layout.appswitcher.f8ab9259');
    if (collapsed) {
      return (
        <Tooltip title={label} placement="right">
          <span
            role="img"
            aria-label={label}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 32, width: '100%' }}
          >
            {ICON_MAP[fixedIcon ?? ''] ?? <AppstoreOutlined />}
          </span>
        </Tooltip>
      );
    }
    return (
      <Space style={{ padding: '0 12px', whiteSpace: 'nowrap' }}>
        {ICON_MAP[fixedIcon ?? ''] ?? <AppstoreOutlined />}
        <Typography.Text strong>{label}</Typography.Text>
        <Tag color="blue">{fixedAppName?.toUpperCase() ?? 'APP'}</Tag>
      </Space>
    );
  }

  const items: MenuProps['items'] = apps.map((a) => ({
    key: a.name,
    icon: ICON_MAP[a.icon ?? ''] ?? <AppstoreOutlined />,
    label: labelOf(a),
    onClick: () => navigateToAppName(a.name, navigate),
  }));

  if (apps.length === 0) return null;

  if (collapsed) {
    const label = current ? labelOf(current) : tx('legacy:layout.appswitcher.f8ab9259');
    return (
      <Dropdown menu={{ items, selectedKeys: current ? [current.name] : [] }} trigger={['click']}>
        <Tooltip title={label} placement="right">
          <Button
            type="text"
            aria-label={label}
            icon={ICON_MAP[current?.icon ?? ''] ?? <AppstoreOutlined />}
            style={{ width: '100%', height: 32 }}
          />
        </Tooltip>
      </Dropdown>
    );
  }

  return (
    <Dropdown menu={{ items, selectedKeys: current ? [current.name] : [] }} trigger={['click']}>
      <Space style={{ cursor: 'pointer', padding: '0 12px', whiteSpace: 'nowrap' }}>
        {ICON_MAP[current?.icon ?? ''] ?? <AppstoreOutlined />}
        <Typography.Text strong>
          {current ? labelOf(current) : tx('legacy:layout.appswitcher.f8ab9259')}
        </Typography.Text>
        <Tag color="blue">{tx('legacy:dynamic.appCount', { count: apps.length })}</Tag>
      </Space>
    </Dropdown>
  );
}
